import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, CheckCircle, Loader2, RefreshCw, Search, X } from "lucide-react";
import { toast } from "react-hot-toast";

interface ProviderQueue {
  id: string;
  name: string;
  description?: string;
}

interface UnassignedQueue {
  id: string;
  name: string;
  phoneNumber: string | null;
}

interface CustomerProfile {
  id: string;
  qboDisplayName: string;
  qboCompanyName?: string;
  genesysQueueNames: string[];
  telephonyQueueKeys?: string[];
}

export function UnassignedQueues() {
  const [unassignedQueues, setUnassignedQueues] = useState<UnassignedQueue[]>([]);
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [search, setSearch] = useState("");

  // Modal state
  const [modalQueue, setModalQueue] = useState<UnassignedQueue | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  const authHeader = { Authorization: `Bearer ${localStorage.getItem("authToken")}` };

  const fetchUnassigned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/v1/forecasting/queues?unassigned=true", {
        headers: authHeader,
      });
      if (!res.ok) throw new Error("Failed to fetch queues");
      const data = await res.json();

      const providerQueues: ProviderQueue[] = data.data?.providerQueues || [];
      const unassignedNames: string[] = data.data?.unassignedQueues || [];

      // Cross-reference to get full queue details
      const result: UnassignedQueue[] = unassignedNames.map((name) => {
        const match = providerQueues.find(
          (q) => q.name.toLowerCase() === name.toLowerCase()
        );
        // Extract phone number from description (e.g. "Number +18005551234")
        const phoneNumber = match?.description?.replace(/^Number\s*/i, "") || null;
        return {
          id: match?.id || name,
          name,
          phoneNumber,
        };
      });

      setUnassignedQueues(result);
    } catch (err) {
      toast.error("Failed to load unassigned queues");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch("/api/v1/customer-profiles?isActive=true&limit=100", {
        headers: authHeader,
      });
      if (!res.ok) return;
      const data = await res.json();
      setCustomers(data.data || []);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    fetchUnassigned();
    fetchCustomers();
  }, [fetchUnassigned, fetchCustomers]);

  const openModal = (queue: UnassignedQueue) => {
    setModalQueue(queue);
    setSelectedCustomerId("");
  };

  const closeModal = () => {
    setModalQueue(null);
    setSelectedCustomerId("");
  };

  const handleAssign = async () => {
    if (!modalQueue || !selectedCustomerId) return;

    const customer = customers.find((c) => c.id === selectedCustomerId);
    if (!customer) return;

    setAssigning(true);
    try {
      // Always add to genesysQueueNames — queue names (not IDs) are stored there
      const body = {
        genesysQueueNames: [...(customer.genesysQueueNames || []), modalQueue.name],
      };

      const res = await fetch(`/api/v1/customer-profiles/${selectedCustomerId}`, {
        method: "PUT",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Failed to assign queue");

      toast.success(`"${modalQueue.name}" assigned to ${customer.qboDisplayName}`);

      // Update local customer state so re-assigning works correctly
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomerId ? { ...c, ...body } : c
        )
      );

      closeModal();
      // Refresh the unassigned list
      fetchUnassigned();
    } catch {
      toast.error("Failed to assign queue. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Unassigned Queues</h1>
            {!loading && (
              unassignedQueues.length === 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                  <CheckCircle className="h-3.5 w-3.5" />
                  All Assigned
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-700">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {unassignedQueues.length} Unassigned
                </span>
              )
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Queues active in Aircall but not assigned to any customer profile
          </p>
        </div>
        <button
          onClick={fetchUnassigned}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Checking queue assignments...</span>
        </div>
      )}

      {/* All assigned — empty state */}
      {!loading && unassignedQueues.length === 0 && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-10 text-center">
          <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
          <p className="mt-3 text-base font-medium text-green-800">
            All queues are assigned
          </p>
          <p className="mt-1 text-sm text-green-600">
            Every active Aircall queue is assigned to at least one customer profile.
          </p>
        </div>
      )}

      {/* Unassigned queues table */}
      {!loading && unassignedQueues.length > 0 && (() => {
        const filteredQueues = unassignedQueues.filter((q) =>
          q.name.toLowerCase().includes(search.toLowerCase()) ||
          (q.phoneNumber || "").includes(search) ||
          q.id.toLowerCase().includes(search.toLowerCase())
        );
        return (
          <div className="rounded-lg border border-orange-200 bg-white overflow-hidden">
            <div className="border-b border-orange-100 bg-orange-50 px-4 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                <p className="text-sm font-medium text-orange-700">
                  The following queues are live in Aircall but have not been assigned to any customer profile.
                </p>
              </div>
              <div className="relative shrink-0">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search queues..."
                  className="rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-52"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Queue Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Phone Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Aircall ID
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredQueues.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                        No queues match &quot;{search}&quot;
                      </td>
                    </tr>
                  ) : (
                    filteredQueues.map((queue) => (
                      <tr key={queue.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {queue.name}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {queue.phoneNumber || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {queue.id}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openModal(queue)}
                            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                          >
                            Assign to Customer
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Assign Modal */}
      {modalQueue && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-40"
            onClick={closeModal}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Assign Queue to Customer
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 rounded-md bg-gray-50 p-3">
              <p className="text-xs text-gray-500 mb-0.5">Queue</p>
              <p className="font-medium text-gray-900">{modalQueue.name}</p>
              {modalQueue.phoneNumber && (
                <p className="text-sm text-gray-500">{modalQueue.phoneNumber}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Customer
              </label>
              <select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">— Choose a customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.qboDisplayName}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssign}
                disabled={!selectedCustomerId || assigning}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {assigning && <Loader2 className="h-4 w-4 animate-spin" />}
                Assign Queue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
